/** @jsx hh */
/** @jsxFrag ChiFragment */
import type { ActionMap, Dispatcher, DispatchOptions, Message } from "@utils/dispatch";
import { ChiFragment, hh } from "@/chi.tsx";
import { createContext } from "@/runtime.tsx";
import { useContext, useMemo, useSelect } from "@/hooks.tsx";

export function createDispatchContext<A extends ActionMap, S, P = undefined>() {
  type D = Dispatcher<A, S, P>;
  const context = createContext<D | null>(null);

  const Provider = ({ dispatcher }: { dispatcher: D }, children: ChiElem) => (
    <context.Provider value={dispatcher}>{children}</context.Provider>
  );

  const useDispatcher = (): D => {
    const dispatcher = useContext(context);
    if (!dispatcher) throw new Error("chi: no dispatcher provided");
    return dispatcher;
  };

  const useDispatch = () => {
    const dispatcher = useDispatcher();
    return useMemo(
      () => (message: Message<A>, options?: DispatchOptions) =>
        dispatcher.dispatch(message, options),
      [dispatcher],
    );
  };

  const useSelector = <T,>(selector: (state: S) => T, isEqual?: (a: T, b: T) => boolean): T =>
    useSelect(useDispatcher(), selector, isEqual);

  const useDispatchState = (): S => useSelector((state) => state);

  return { context, Provider, useDispatcher, useDispatch, useSelector, useDispatchState };
}

export const globalDispatch = createDispatchContext<any, any, any>();

export const {
  Provider: DispatchProvider,
  useDispatcher,
  useDispatch,
  useSelector,
  useDispatchState,
} = globalDispatch;
